import _mergeJSXProps from "@vue/babel-helper-vue-jsx-merge-props";
// Types
import mixins from "vue-typed-mixins";
// Mixins
import EditorBase, { isElementComponent } from '../../mixins/EditorBase';
import { WithRefs } from '../../utils/withRefs';
import TicketProvider from '../../mixins/TicketProvider';

//Components
import AdminButton from '../ui/AdminButton';
import CmsAltMenu from '../ui/CmsAltMenu';
import EditorListItem from './EditorListItem';
import AdminGlassPanel from '../ui/AdminGlassPanel';
import AdminInputContainer from '../ui/AdminInputContainer';
import AdminInsetBox from '../ui/AdminInsetBox';

// Utils
import { checkedFromInput, preventDefault, valueFromInput } from '../../utils/jsxHelpers';
// Enums
import { TicketProviders } from '../../enums/TicketProvider';
import { WithPartialInject } from '../../utils/withInject';
import { CmsAltMenuGroup } from '../../enums/CmsAltMenuGroup';
const dummyInject = WithPartialInject('dummyMode');
export let MoveDirection;
(function (MoveDirection) {
  MoveDirection[MoveDirection["Backward"] = -1] = "Backward";
  MoveDirection[MoveDirection["Forward"] = 1] = "Forward";
})(MoveDirection || (MoveDirection = {}));
const isGrouped = (items, groupByLetters) => groupByLetters;
const EditorList = mixins(TicketProvider(TicketProviders.EditorList), EditorBase(() => []), WithRefs(), dummyInject).extend({
  name: 'EditorList',
  components: {
    EditorTipTap: () => import('./EditorTipTap'),
    EditorLink: () => import('./EditorLink'),
    EditorImage: () => import('./EditorImage')
  },
  provide() {
    return {
      EditorListInstance: this
    };
  },
  props: {
    itemOptions: {
      type: Array,
      default: () => []
    },
    itemClass: {
      type: String,
      default: ''
    },
    addButtonClass: {
      type: String,
      default: ''
    },
    sortBy: {
      type: String,
      default: ''
    },
    sortOrderDescending: {
      type: Boolean,
      default: false
    },
    groupByLetters: {
      type: Boolean,
      default: false
    },
    filteredLetter: {
      type: String,
      default: ''
    },
    limit: {
      type: [String, Number],
      default: ''
    }
  },
  data() {
    const form = {
      $id: 0
    };
    this.itemOptions.forEach(option => {
      form[option.name] = null;
    });
    return {
      form,
      lastId: 1
    };
  },
  computed: {
    sectionId() {
      if (!this.componentData) return;
      if (isElementComponent(this.componentData)) return this.componentData.id;
      return this.componentData.linkId;
    },
    items() {
      if (this.dummyMode) return Array.from(Array(6)).map((_, i) => ({
        $id: i
      }));
      const value = this.computedValue || [];
      const {
        sortOrderDescending,
        sortBy,
        filteredLetter
      } = this;
      //Don't allow sorting by coordinates
      // TODO: What? - Dominik, 7.5.2021.
      const disableOrderingByTypes = value.some(({
        name,
        type
      }) => name === sortBy && (type === 'coordinates' || type === 'checkbox'));
      if (!sortBy || disableOrderingByTypes) return value;
      const filtered = filteredLetter ? value.filter(v => String(v[this.sortBy] || '').toLowerCase()[0] === String(filteredLetter || '').toLowerCase()) : value;
      const sorted = filtered.sort((a, b) => (sortOrderDescending ? (a[sortBy] || 0) > (b[sortBy] || 0) : (a[sortBy] || 0) < (b[sortBy] || 0)) ? -1 : 1);
      if (!this.groupByLetters) return sorted;
      const groupedByLetters = [];
      sorted.forEach(item => {
        const letter = String(item[this.sortBy] || '').toLowerCase()[0];
        const group = groupedByLetters.find(group => group.letter === letter);
        if (group) {
          group.items.push(item);
        } else {
          groupedByLetters.push({
            letter,
            items: [item]
          });
        }
      });
      return groupedByLetters;
    },
    imageOptionNames() {
      return this.itemOptions.filter(option => option.type === 'image').map(option => `#id-${this.form.$id}-${this.computedName}-${option.name}-${this.sectionId || ''}-menu`);
    }
  },
  created() {
    var _this$computedValue;
    this.computedValue = ((_this$computedValue = this.computedValue) === null || _this$computedValue === void 0 ? void 0 : _this$computedValue.map(value => {
      if (value.$id && value.$id >= this.lastId) {
        this.lastId = value.$id + 1;
        return value;
      }
      value.$id = this.lastId++;
      return value;
    })) || [];
    this.$emit("items-loaded", this.items);
  },
  methods: {
    getFlattenedItems() {
      return !isGrouped(this.items, this.groupByLetters) ? this.items : this.items.reduce((acc, item) => acc.concat(item.items), []);
    },
    genEditorImageId(options, itemIndex = -1) {
      var _this$getFlattenedIte;
      return `id-${((_this$getFlattenedIte = this.getFlattenedItems()[itemIndex]) === null || _this$getFlattenedIte === void 0 ? void 0 : _this$getFlattenedIte.$id) || ''}-${this.computedName}-${options.name}-${this.sectionId || ''}`;
    },
    genField(options, itemIndex = -1) {
      var _this$form$options$na, _this$form$options$na2;
      const h = this.$createElement;
      const index = this.itemOptions.indexOf(options);
      const key = `${this.sectionId || ''}-item-${itemIndex === -1 ? 'new' : itemIndex}-${options.name}`;

      /** Add ref and shift + tab handler on the first field to trap focus */
      const empower = content => {
        content.data = content.data || {};
        const {
          data
        } = content;
        data.domProps = data.domProps || {};
        data.domProps.id = key;
        if (index !== 0) return content;
        data.ref = 'firstElement' + itemIndex;
        data.on = data.on || {};
        const keydownHandler = e => {
          var _this$$refs;
          if (!(e.shiftKey && e.code === 'Tab')) return;
          (_this$$refs = this.$refs['lastElement' + itemIndex]) === null || _this$$refs === void 0 ? void 0 : _this$$refs.focus();
          e.preventDefault();
        };
        data.on.keydown = [keydownHandler];
        return content;
      };
      const wrap = (content, containerClasses = '') => {
        var _content$data, _content$data$class;
        return h(AdminInputContainer, {
          "key": key,
          "class": containerClasses,
          "attrs": {
            "label": options.label,
            "for": key,
            "noFieldWrap": (_content$data = content.data) === null || _content$data === void 0 ? void 0 : (_content$data$class = _content$data.class) === null || _content$data$class === void 0 ? void 0 : _content$data$class.includes('no-field-wrap')
          }
        }, [empower(content)]);
      };
      const value = this.form[options.name];
      const fields = {
        text: h("input", {
          "attrs": {
            "type": "text"
          },
          "class": "px-2 py-1 w-full bg-transparent",
          "domProps": {
            "value": typeof value === 'string' ? value : ''
          },
          "on": {
            "input": valueFromInput(e => this.form[options.name] = e)
          }
        }),
        number: h("input", {
          "attrs": {
            "type": "number"
          },
          "class": "px-2 py-1 w-full bg-transparent",
          "domProps": {
            "value": typeof value === 'number' ? value : ''
          },
          "on": {
            "input": valueFromInput(e => this.form[options.name] = Number(e))
          }
        }),
        textarea: h("textarea", {
          "attrs": {
            "rows": 3
          },
          "class": "px-2 py-1 w-full block bg-transparent",
          "domProps": {
            "value": typeof value === 'string' ? value : ''
          },
          "on": {
            "input": valueFromInput(e => this.form[options.name] = e)
          }
        }),
        checkbox: h("input", {
          "attrs": {
            "type": "checkbox",
            "id": options.name
          },
          "class": "px-2 py-1 w-full bg-transparent",
          "domProps": {
            "checked": typeof value === 'boolean' ? value : false
          },
          "on": {
            "input": checkedFromInput(v => this.form[options.name] = v)
          }
        }),
        tipTap: h("editor-tip-tap", {
          "attrs": {
            "hideBoxStyling": true,
            "full": true,
            "value": this.form[options.name],
            "contentClass": "w-full px-2 py-1"
          },
          "on": {
            "input": e => this.form[options.name] = e
          }
        }),
        link: h("editor-link", {
          "attrs": {
            "value": this.form[options.name],
            "buttonClass": "rounded-r-sm border border-gray-300",
            "block": true
          },
          "on": {
            "input": e => this.form[options.name] = e
          },
          "scopedSlots": {
            default: ({
              item
            }) => h("div", {
              "class": "px-2 py-1 h-8"
            }, [item === null || item === void 0 ? void 0 : item.name])
          }
        }),
        image: h("div", {
          "class": "relative"
        }, [h("editor-image", {
          "attrs": {
            "id": this.genEditorImageId(options, itemIndex),
            "inBackground": true,
            "size": options.size,
            "buttonClass": "rounded-sm border border-gray-300",
            "value": this.form[options.name]
          },
          "class": "bg-cover",
          "on": {
            "input": media => this.form[options.name] = media
          }
        }), h("div", {
          "class": "pointer-events-none rounded-sm absolute top-0 right-0 bottom-0 left-0 shadow-inner"
        })]),
        coordinates: h("div", {
          "class": "flex items-center space-x-4 no-field-wrap"
        }, [h(AdminInsetBox, {
          "class": 'w-1/2 min-w-0'
        }, [h("input", {
          "attrs": {
            "type": "number",
            "step": "0.0000000001",
            "placeholder": "Lat",
            "min": -180,
            "max": 180
          },
          "class": "px-2 py-1 bg-transparent",
          "domProps": {
            "value": ((_this$form$options$na = this.form[options.name]) === null || _this$form$options$na === void 0 ? void 0 : _this$form$options$na.lat) || ''
          },
          "on": {
            "input": valueFromInput(e => this.form[options.name].lat = e)
          }
        })]), h(AdminInsetBox, {
          "class": 'w-1/2 min-w-0'
        }, [h("input", {
          "attrs": {
            "type": "number",
            "step": "0.0000000001",
            "placeholder": "Long",
            "min": -90,
            "max": 90
          },
          "class": "px-2 py-1 bg-transparent",
          "domProps": {
            "value": ((_this$form$options$na2 = this.form[options.name]) === null || _this$form$options$na2 === void 0 ? void 0 : _this$form$options$na2.long) || ''
          },
          "on": {
            "input": valueFromInput(e => this.form[options.name].long = e)
          }
        })])])
      };
      return wrap(fields[options.type] || h("div", {
        "class": "inline-block w-2/4"
      }, ["Unsupported type ", h("b", [options.type])]), fields[options.type] ? '' : 'bg-red-200 rounded-md');
    },
    createForm(itemIndex = -1) {
      var _this$computedValue2, _this$$refs2, _ref;
      const form = {
        $id: itemIndex !== -1 && ((_this$computedValue2 = this.computedValue) === null || _this$computedValue2 === void 0 ? void 0 : _this$computedValue2[itemIndex].$id) || 0
      };
      this.itemOptions.forEach(({
        name,
        type
      }) => {
        var _this$computedValue3;
        const defaultValue = type === 'coordinates' ? {
          lat: '',
          long: ''
        } : null;
        form[name] = itemIndex !== -1 && ((_this$computedValue3 = this.computedValue) === null || _this$computedValue3 === void 0 ? void 0 : _this$computedValue3[itemIndex][name]) || defaultValue;
      });
      Object.assign(this.form, form);
      (_this$$refs2 = this.$refs[(_ref = 'firstElement' + itemIndex) !== null && _ref !== void 0 ? _ref : -1]) === null || _this$$refs2 === void 0 ? void 0 : _this$$refs2.focus();
    },
    addItem() {
      const newValue = this.computedValue || [];
      this.computedValue = [...newValue, {
        ...this.form,
        $id: this.form.$id || this.lastId++
      }];
      this.$emit("items-loaded", this.items);
      return newValue;
    },
    updateItem(index) {
      const def = this.computedValue || [];
      const newValue = [...def];
      newValue.splice(index, 1, {
        ...this.form
      });
      this.computedValue = newValue;
      this.$emit("items-loaded", this.items);
      return newValue;
    },
    removeItem(itemIndex) {
      const def = this.computedValue || [];
      const newValue = [...def];
      newValue.splice(itemIndex, 1);
      this.computedValue = newValue;
      this.$emit("items-loaded", this.items);
      return newValue;
    },
    async moveItem(itemIndex, direction) {
      if (!this.computedValue) return;
      const def = this.computedValue;
      const newValue = [...def];
      if (direction === MoveDirection.Forward) {
        const temp = this.computedValue[itemIndex + 1];
        if (!temp) return;
        newValue.splice(itemIndex + 1, 1);
        newValue.splice(itemIndex, 0, temp);
      } else {
        const temp = this.computedValue[itemIndex - 1];
        if (!temp) return;
        newValue.splice(itemIndex - 1, 1);
        newValue.splice(itemIndex, 0, temp);
      }
      this.computedValue = newValue;
      this.$emit("items-loaded", this.items);
      return newValue;
    }
  },
  render() {
    var _this$$scopedSlots$ad, _this$$scopedSlots, _this$$scopedSlots$ad2;
    const h = arguments[0];
    const listSlot = this.$scopedSlots.list;

    // TODO: maybe document this feature if I decide to leave it
    const s = (_this$$scopedSlots$ad = (_this$$scopedSlots = this.$scopedSlots)['add-button']) === null || _this$$scopedSlots$ad === void 0 ? void 0 : (_this$$scopedSlots$ad2 = _this$$scopedSlots$ad.call(_this$$scopedSlots, {})) === null || _this$$scopedSlots$ad2 === void 0 ? void 0 : _this$$scopedSlots$ad2[0];
    const activator = !s ? h(AdminButton, {
      "slot": "activator",
      "class": ['mt-2']
    }, [this.$at('core.action.add')]) : this.$createElement(s.tag, s.data ? {
      ...s.data,
      slot: 'activator'
    } : {
      slot: 'activator'
    }, s.children);
    const {
      items,
      limit
    } = this;
    const limitedItems = limit ?
    // @ts-ignore
    items.filter((item, index) => index + 1 <= limit) : items;
    return h("div", [listSlot ? listSlot({
      items: limitedItems
    }) : isGrouped(items, this.groupByLetters) ? // @ts-ignore
    limitedItems === null || limitedItems === void 0 ? void 0 : limitedItems.map((item, index) => {
      var _this$$scopedSlots$de, _this$$scopedSlots2, _this$$scopedSlots$de2;
      return (_this$$scopedSlots$de = (_this$$scopedSlots2 = this.$scopedSlots).default) === null || _this$$scopedSlots$de === void 0 ? void 0 : (_this$$scopedSlots$de2 = _this$$scopedSlots$de.call(_this$$scopedSlots2, {
        item,
        ...item,
        index
      })) === null || _this$$scopedSlots$de2 === void 0 ? void 0 : _this$$scopedSlots$de2.map(slot => ({
        ...slot,
        key: item.letter
      }));
    }) : // @ts-ignore
    limitedItems === null || limitedItems === void 0 ? void 0 : limitedItems.map((item, itemIndex) => {
      var _this$$scopedSlots$de3, _this$$scopedSlots3;
      return h(EditorListItem, {
        "attrs": {
          "itemId": item.$id
        },
        "class": this.itemClass,
        "key": `${this.computedName}-${item.$id}`
      }, [(_this$$scopedSlots$de3 = (_this$$scopedSlots3 = this.$scopedSlots).default) === null || _this$$scopedSlots$de3 === void 0 ? void 0 : _this$$scopedSlots$de3.call(_this$$scopedSlots3, {
        item,
        index: itemIndex
      })]);
    }), this.$isInEditMode && h(CmsAltMenu, {
      "class": this.addButtonClass,
      "on": {
        "opened": () => this.createForm()
      },
      "attrs": {
        "disabled": !this.$isInEditMode,
        "disableClickOutside": true,
        "group": CmsAltMenuGroup.EditorList
      },
      "scopedSlots": {
        opener: () => limit && limit <= items.length ? null : activator,
        default: ({
          close
        }) => h(AdminGlassPanel, {
          "attrs": {
            "light": true,
            "padding": 'p-2'
          }
        }, [h("form", {
          "style": "max-width: 900px; width: 96%",
          "on": {
            "submit": preventDefault(() => this.addItem() && close())
          }
        }, [this.itemOptions.map(options => this.genField(options)), h("div", {
          "class": "flex space-x-4 justify-end p-2"
        }, [h(AdminButton, {
          "attrs": {
            "secondary": true
          },
          "on": {
            "click": () => close()
          }
        }, [this.$at('core.action.cancel')]), h(AdminButton, _mergeJSXProps([{
          "ref": "lastElement",
          "attrs": {
            "type": 'submit'
          }
        }, {
          "nativeOn": {
            keydown: e => {
              if (!e.shiftKey && e.code === 'Tab') {
                var _this$$refs$firstElem;
                e.preventDefault();
                (_this$$refs$firstElem = this.$refs.firstElement) === null || _this$$refs$firstElem === void 0 ? void 0 : _this$$refs$firstElem.focus();
              }
            }
          }
        }]), [this.$at('core.action.save')])])])])
      }
    })]);
  }
});
export default EditorList;